const KEY_MATCHES = 'fps_career_matches';
const KEY_WINS = 'fps_career_wins';
const KEY_KILLS = 'fps_career_kills';
const KEY_TOP3 = 'fps_career_top3';
const KEY_ARENA = 'fps_career_arena_points';
const KEY_RANKED_PLACEMENT = 'fps_career_ranked_placement_pts';
const KEY_TOP5 = 'fps_career_top5';
const KEY_DEATHS = 'fps_career_deaths';
const KEY_HEADSHOTS = 'fps_career_headshots';
const KEY_SHOTS = 'fps_career_shots';
const KEY_LONGEST_SNIPE = 'fps_career_longest_snipe';
const KEY_MATCH_HISTORY = 'fps_career_match_history_v1';
const MAX_MATCH_HISTORY = 10;

export type MatchHistoryEntry = {
  at: number;
  won: boolean;
  kills: number;
  placement: number;
  ranked: boolean;
};

export type CareerStats = {
  matches: number;
  wins: number;
  kills: number;
  top3: number;
  arenaPoints: number;
};

export type ArenaRank = 'Bronze' | 'Silver' | 'Gold' | 'Diamond';

export type ArenaMatchResult = {
  pointsEarned: number;
  arenaPoints: number;
  rank: ArenaRank;
};

export type RankedPlacementResult = {
  delta: number;
  total: number;
};

export type ArenaRankProgress = {
  rank: ArenaRank;
  nextRank: ArenaRank | null;
  progress: number;
  needed: number;
  pct: number;
};

const ARENA_THRESHOLDS: { rank: ArenaRank; min: number }[] = [
  { rank: 'Bronze', min: 0 },
  { rank: 'Silver', min: 100 },
  { rank: 'Gold', min: 300 },
  { rank: 'Diamond', min: 600 },
];

function readInt(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeInt(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(Math.max(0, Math.floor(value))));
  } catch {
    /* ignore quota / private mode */
  }
}

function readFloat(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    const n = raw ? parseFloat(raw) : 0;
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeFloat(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(Math.max(0, value)));
  } catch {
    /* ignore quota / private mode */
  }
}

function readMatchHistory(): MatchHistoryEntry[] {
  try {
    const raw = localStorage.getItem(KEY_MATCH_HISTORY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as MatchHistoryEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (e) =>
          e &&
          typeof e.at === 'number' &&
          typeof e.won === 'boolean' &&
          typeof e.kills === 'number' &&
          typeof e.placement === 'number',
      )
      .slice(0, MAX_MATCH_HISTORY);
  } catch {
    return [];
  }
}

function writeMatchHistory(entries: MatchHistoryEntry[]): void {
  try {
    localStorage.setItem(KEY_MATCH_HISTORY, JSON.stringify(entries.slice(0, MAX_MATCH_HISTORY)));
  } catch {
    /* ignore quota / private mode */
  }
}

function formatMatchDate(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Persistent local career totals for the FPS mode. */
export class FpsCareerStats {
  // ─── panel DOM reference ───────────────────────────────────────────────────
  private static _panel: HTMLElement | null = null;
  private static _escHandler: ((e: KeyboardEvent) => void) | null = null;

  // ─── existing load / save ──────────────────────────────────────────────────

  static load(): CareerStats {
    return {
      matches: readInt(KEY_MATCHES),
      wins: readInt(KEY_WINS),
      kills: readInt(KEY_KILLS),
      top3: readInt(KEY_TOP3),
      arenaPoints: readInt(KEY_ARENA),
    };
  }

  static save(stats: CareerStats): void {
    writeInt(KEY_MATCHES, stats.matches);
    writeInt(KEY_WINS, stats.wins);
    writeInt(KEY_KILLS, stats.kills);
    writeInt(KEY_TOP3, stats.top3);
    writeInt(KEY_ARENA, stats.arenaPoints);
  }

  // ─── granular stat recorders ───────────────────────────────────────────────

  /** Increment total shots fired (used for headshot % denominator). */
  static recordShot(): void {
    writeInt(KEY_SHOTS, readInt(KEY_SHOTS) + 1);
  }

  /** Increment headshot kills. */
  static recordHeadshot(): void {
    writeInt(KEY_HEADSHOTS, readInt(KEY_HEADSHOTS) + 1);
  }

  /** Increment player deaths. */
  static recordDeath(): void {
    writeInt(KEY_DEATHS, readInt(KEY_DEATHS) + 1);
  }

  /** Store distance (in meters) if it exceeds the current record. */
  static recordSnipeDistance(dist: number): void {
    if (!Number.isFinite(dist) || dist <= 0) return;
    const current = readFloat(KEY_LONGEST_SNIPE);
    if (dist > current) writeFloat(KEY_LONGEST_SNIPE, dist);
  }

  /** Record a top-5 finish (separate from top-3 legacy key). */
  static recordTop5(): void {
    writeInt(KEY_TOP5, readInt(KEY_TOP5) + 1);
  }

  // ─── derived stat getters ──────────────────────────────────────────────────

  /** Headshot percentage (0–100). Requires recordShot() on every bullet fired. */
  static getHeadshotPct(): number {
    const shots = readInt(KEY_SHOTS);
    if (shots === 0) return 0;
    return Math.min(100, (readInt(KEY_HEADSHOTS) / shots) * 100);
  }

  /** Longest confirmed snipe distance in meters. */
  static getLongestSnipeM(): number {
    return readFloat(KEY_LONGEST_SNIPE);
  }

  /** Kill/death ratio. Deaths floored at 1 to avoid division by zero. */
  static getKD(): number {
    const kills = readInt(KEY_KILLS);
    const deaths = Math.max(1, readInt(KEY_DEATHS));
    return kills / deaths;
  }

  /** Top-5 finish count. */
  static getTop5Count(): number {
    return readInt(KEY_TOP5);
  }

  /** Last 10 recorded matches (newest first). */
  static getMatchHistory(): MatchHistoryEntry[] {
    return readMatchHistory();
  }

  private static pushMatchHistory(entry: Omit<MatchHistoryEntry, 'at'>): void {
    const next: MatchHistoryEntry = { ...entry, at: Date.now() };
    const history = readMatchHistory();
    history.unshift(next);
    writeMatchHistory(history.slice(0, MAX_MATCH_HISTORY));
  }

  // ─── arena helpers (unchanged) ─────────────────────────────────────────────

  static getArenaRank(points: number): ArenaRank {
    if (points >= 600) return 'Diamond';
    if (points >= 300) return 'Gold';
    if (points >= 100) return 'Silver';
    return 'Bronze';
  }

  /** Progress within current rank tier toward the next threshold (Bronze→Silver→Gold→Diamond). */
  static getArenaRankProgress(points: number): ArenaRankProgress {
    const pts = Math.max(0, Math.floor(points));
    const rank = this.getArenaRank(pts);
    const idx = ARENA_THRESHOLDS.findIndex((t) => t.rank === rank);
    const currentMin = ARENA_THRESHOLDS[idx]?.min ?? 0;
    const next = idx >= 0 && idx < ARENA_THRESHOLDS.length - 1 ? ARENA_THRESHOLDS[idx + 1]! : null;
    if (!next) {
      return {
        rank,
        nextRank: null,
        progress: pts - currentMin,
        needed: 0,
        pct: 100,
      };
    }
    const progress = pts - currentMin;
    const needed = next.min - currentMin;
    const pct = needed > 0 ? Math.min(100, Math.max(0, (progress / needed) * 100)) : 100;
    return { rank, nextRank: next.rank, progress, needed, pct };
  }

  static arenaRankColor(rank: ArenaRank): string {
    switch (rank) {
      case 'Diamond':
        return '#7ef0ff';
      case 'Gold':
        return '#ffd54a';
      case 'Silver':
        return '#c8d4e8';
      default:
        return '#cd7f32';
    }
  }

  /** Ranked placement points delta shown on the end screen (+/-). */
  static placementPointsForMatch(opts: {
    placement: number;
    totalPlayers: number;
    won: boolean;
    ranked?: boolean;
  }): number {
    if (!opts.ranked) return 0;
    if (opts.won) return 50;
    if (opts.placement <= 3) return 22;
    if (opts.placement <= 5) return 10;
    if (opts.placement <= 10) return 2;
    const over = opts.placement - 10;
    return -Math.min(28, 4 + over * 3);
  }

  static formatPlacementPointsDelta(delta: number): string {
    if (delta > 0) return `+${delta} Ranked Points`;
    if (delta < 0) return `${delta} Ranked Points`;
    return '±0 Ranked Points';
  }

  static placementPointsColor(delta: number): string {
    if (delta > 0) return '#7ef9a8';
    if (delta < 0) return '#ff7a7a';
    return '#9ab0c8';
  }

  static loadRankedPlacementPoints(): number {
    return readInt(KEY_RANKED_PLACEMENT);
  }

  static recordRankedPlacement(opts: {
    placement: number;
    totalPlayers: number;
    won: boolean;
    ranked?: boolean;
  }): RankedPlacementResult {
    const delta = this.placementPointsForMatch(opts);
    if (!opts.ranked) {
      return { delta: 0, total: this.loadRankedPlacementPoints() };
    }
    const total = Math.max(0, this.loadRankedPlacementPoints() + delta);
    writeInt(KEY_RANKED_PLACEMENT, total);
    return { delta, total };
  }

  /** Win +50, top 3 +25, each kill +5. Ranked matches earn ×1.5 AP. */
  static arenaPointsForMatch(opts: {
    won: boolean;
    kills: number;
    placement: number;
    ranked?: boolean;
  }): number {
    let pts = Math.max(0, opts.kills) * 5;
    if (opts.won) pts += 50;
    else if (opts.placement <= 3) pts += 25;
    if (opts.ranked) pts = Math.floor(pts * 1.5);
    return pts;
  }

  static recordMatch(opts: {
    won: boolean;
    kills: number;
    placement: number;
    ranked?: boolean;
  }): CareerStats {
    const stats = this.load();
    stats.matches += 1;
    stats.kills += Math.max(0, opts.kills);
    if (opts.won) stats.wins += 1;
    if (opts.placement <= 3) stats.top3 += 1;
    if (opts.placement <= 5) this.recordTop5();
    const arenaGain = this.arenaPointsForMatch(opts);
    stats.arenaPoints += arenaGain;
    this.save(stats);
    this.pushMatchHistory({
      won: opts.won,
      kills: Math.max(0, opts.kills),
      placement: Math.max(1, Math.floor(opts.placement)),
      ranked: !!opts.ranked,
    });
    return stats;
  }

  static recordArenaMatch(opts: {
    won: boolean;
    kills: number;
    placement: number;
    ranked?: boolean;
  }): ArenaMatchResult {
    const stats = this.recordMatch(opts);
    const pointsEarned = this.arenaPointsForMatch(opts);
    return {
      pointsEarned,
      arenaPoints: stats.arenaPoints,
      rank: this.getArenaRank(stats.arenaPoints),
    };
  }

  // ─── Career Card DOM panel ─────────────────────────────────────────────────

  static isVisible(): boolean {
    return !!this._panel && this._panel.style.display !== 'none';
  }

  static showPanel(): void {
    if (this._panel) {
      this._panel.remove();
      this._panel = null;
    }

    const career = this.load();
    const rank = this.getArenaRank(career.arenaPoints);
    const rankColor = this.arenaRankColor(rank);
    const winPct = career.matches > 0 ? ((career.wins / career.matches) * 100).toFixed(1) : '0.0';
    const kd = this.getKD().toFixed(2);
    const hsPct = this.getHeadshotPct().toFixed(1);
    const snipe = this.getLongestSnipeM().toFixed(1);
    const top5 = this.getTop5Count();

    const overlay = document.createElement('div');
    overlay.id = 'career-stats-overlay';
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 8500;
      background: rgba(0,0,0,0.82);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Segoe UI', system-ui, sans-serif;
    `;

    const card = document.createElement('div');
    card.style.cssText = `
      background: #111827;
      border: 1px solid #2d3748;
      border-radius: 12px;
      padding: 32px 40px;
      min-width: 380px;
      max-width: 520px;
      width: 100%;
      max-height: min(88vh, 720px);
      overflow-y: auto;
      box-shadow: 0 24px 64px rgba(0,0,0,0.7);
      position: relative;
    `;

    const title = document.createElement('div');
    title.textContent = 'CAREER STATS';
    title.style.cssText = `
      color: #ffd54a;
      font-size: 22px;
      font-weight: 800;
      letter-spacing: 3px;
      text-align: center;
      margin-bottom: 28px;
      text-shadow: 0 0 12px rgba(255,213,74,0.4);
    `;

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '✕';
    closeBtn.style.cssText = `
      position: absolute;
      top: 14px;
      right: 18px;
      background: none;
      border: none;
      color: #718096;
      font-size: 18px;
      cursor: pointer;
      line-height: 1;
      padding: 4px 8px;
      border-radius: 4px;
      transition: color 0.15s;
    `;
    closeBtn.onmouseenter = () => { closeBtn.style.color = '#e2e8f0'; };
    closeBtn.onmouseleave = () => { closeBtn.style.color = '#718096'; };
    closeBtn.onclick = () => this.hidePanel();

    const rows: [string, string, string?][] = [
      ['Matches Played', String(career.matches)],
      ['Wins', String(career.wins)],
      ['Win %', `${winPct}%`],
      ['Total Kills', String(career.kills)],
      ['K/D Ratio', kd],
      ['Headshot %', `${hsPct}%`],
      ['Top 5 Finishes', String(top5)],
      [`Longest Snipe`, `${snipe} m`],
      ['Arena Rank', rank, rankColor],
    ];

    const grid = document.createElement('div');
    grid.style.cssText = `display: flex; flex-direction: column; gap: 10px;`;

    rows.forEach(([label, value, color]) => {
      const row = document.createElement('div');
      row.style.cssText = `
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background: #1a2332;
        border-radius: 6px;
        border: 1px solid #243044;
      `;
      const lbl = document.createElement('span');
      lbl.textContent = label;
      lbl.style.cssText = `color: #718096; font-size: 13px; font-weight: 500;`;

      const val = document.createElement('span');
      val.textContent = value;
      val.style.cssText = `
        color: ${color ?? '#e2e8f0'};
        font-size: 15px;
        font-weight: 700;
        letter-spacing: 0.5px;
      `;

      row.appendChild(lbl);
      row.appendChild(val);
      grid.appendChild(row);
    });

    const historyTitle = document.createElement('div');
    historyTitle.textContent = 'RECENT MATCHES';
    historyTitle.style.cssText = `
      color: #9ec4ff;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 2px;
      margin: 22px 0 10px;
      text-align: center;
    `;

    const historyList = document.createElement('div');
    historyList.style.cssText = `display: flex; flex-direction: column; gap: 6px;`;
    const history = this.getMatchHistory();
    if (history.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No matches recorded yet';
      empty.style.cssText = `color: #4a5568; font-size: 12px; text-align: center; padding: 10px 0;`;
      historyList.appendChild(empty);
    } else {
      history.forEach((entry) => {
        const row = document.createElement('div');
        row.style.cssText = `
          display: grid;
          grid-template-columns: 52px 1fr auto auto;
          gap: 8px;
          align-items: center;
          padding: 7px 10px;
          background: #141c28;
          border-radius: 6px;
          border: 1px solid #243044;
          font-size: 12px;
        `;
        const dateEl = document.createElement('span');
        dateEl.textContent = formatMatchDate(entry.at);
        dateEl.style.cssText = `color: #718096; font-weight: 600;`;
        const resultEl = document.createElement('span');
        resultEl.textContent = entry.won ? 'Victory Royale' : `#${entry.placement}`;
        resultEl.style.cssText = `color: ${entry.won ? '#ffd54a' : '#e2e8f0'}; font-weight: 700;`;
        const killsEl = document.createElement('span');
        killsEl.textContent = `${entry.kills} elims`;
        killsEl.style.cssText = `color: #ff8a8a; font-weight: 700; text-align: right;`;
        const rankEl = document.createElement('span');
        rankEl.textContent = entry.ranked ? 'Ranked' : 'Casual';
        rankEl.style.cssText = `color: #8899bb; font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;`;
        row.appendChild(dateEl);
        row.appendChild(resultEl);
        row.appendChild(killsEl);
        row.appendChild(rankEl);
        historyList.appendChild(row);
      });
    }

    const hint = document.createElement('div');
    hint.textContent = 'Press ESC to close';
    hint.style.cssText = `
      text-align: center;
      color: #4a5568;
      font-size: 11px;
      margin-top: 20px;
      letter-spacing: 1px;
    `;

    card.appendChild(closeBtn);
    card.appendChild(title);
    card.appendChild(grid);
    card.appendChild(historyTitle);
    card.appendChild(historyList);
    card.appendChild(hint);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    this._panel = overlay;

    // Close on overlay backdrop click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.hidePanel();
    });

    // ESC key handler
    this._escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.hidePanel();
    };
    document.addEventListener('keydown', this._escHandler);
  }

  static hidePanel(): void {
    if (this._panel) {
      this._panel.remove();
      this._panel = null;
    }
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
      this._escHandler = null;
    }
  }
}
